import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './PacmanChase.css';

const GRID_SIZE = 15;
const CELL_EMPTY = 0;
const CELL_WALL = 1;
const CELL_DOT = 2;
const CELL_POWER_DOT = 5;

const PacmanChase = () => {
  const [grid, setGrid] = useState([]);
  const [playerPos, setPlayerPos] = useState({ x: 1, y: 1 });
  const [ghosts, setGhosts] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [isInvisible, setIsInvisible] = useState(false);
  const [invisibilityTimer, setInvisibilityTimer] = useState(0);

  // Refs to access current state in intervals
  const playerPosRef = useRef({ x: 1, y: 1 });
  const gridRef = useRef([]);

  // Update refs when state changes
  useEffect(() => {
    playerPosRef.current = playerPos;
  }, [playerPos]);

  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);

  const generateGrid = () => {
    // Start with all walls
    const newGrid = Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(CELL_WALL));
    
    // Use recursive backtracking to create connected paths
    const stack = [];
    const visited = Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(false));
    
    // Start from position (1,1)
    let currentX = 1;
    let currentY = 1;
    newGrid[currentY][currentX] = CELL_DOT;
    visited[currentY][currentX] = true;
    stack.push([currentX, currentY]);
    
    const directions = [
      [0, -2], // up
      [2, 0],  // right
      [0, 2],  // down
      [-2, 0]  // left
    ];
    
    while (stack.length > 0) {
      const neighbors = [];
      
      // Find unvisited neighbors (2 cells away)
      for (const [dx, dy] of directions) {
        const newX = currentX + dx;
        const newY = currentY + dy;
        
        if (newX > 0 && newX < GRID_SIZE - 1 && 
            newY > 0 && newY < GRID_SIZE - 1 && 
            !visited[newY][newX]) {
          neighbors.push([newX, newY, dx, dy]);
        }
      }
      
      if (neighbors.length > 0) {
        // Choose random neighbor
        const [nextX, nextY, dx, dy] = neighbors[Math.floor(Math.random() * neighbors.length)];
        
        // Create path to neighbor (carve through the wall between)
        newGrid[currentY + dy / 2][currentX + dx / 2] = CELL_DOT;
        newGrid[nextY][nextX] = CELL_DOT;
        visited[nextY][nextX] = true;
        
        stack.push([nextX, nextY]);
        currentX = nextX;
        currentY = nextY;
      } else {
        // Backtrack
        [currentX, currentY] = stack.pop();
      }
    }
    
    // Add some additional random connections to make it more interesting
    for (let i = 0; i < 5; i++) {
      const x = Math.floor(Math.random() * (GRID_SIZE - 2)) + 1;
      const y = Math.floor(Math.random() * (GRID_SIZE - 2)) + 1;
      if (newGrid[y][x] === CELL_WALL) {
        // Check if connecting this cell would connect two path areas
        const neighbors = [
          [x, y-1], [x, y+1], [x-1, y], [x+1, y]
        ];
        const pathNeighbors = neighbors.filter(([nx, ny]) => 
          nx >= 0 && nx < GRID_SIZE && ny >= 0 && ny < GRID_SIZE && 
          newGrid[ny][nx] === CELL_DOT
        );
        if (pathNeighbors.length >= 2) {
          newGrid[y][x] = CELL_DOT;
        }
      }
    }
    
    // Ensure player starting position is clear
    newGrid[1][1] = CELL_EMPTY;
    
    // Add 3 random power dots in accessible areas
    const pathCells = [];
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        if (newGrid[y][x] === CELL_DOT && !(x === 1 && y === 1)) {
          pathCells.push({ x, y });
        }
      }
    }
    
    // Place 3 power dots randomly
    for (let i = 0; i < 3 && pathCells.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * pathCells.length);
      const { x, y } = pathCells[randomIndex];
      newGrid[y][x] = CELL_POWER_DOT;
      pathCells.splice(randomIndex, 1);
    }
    
    return newGrid;
  };

  const generateGhosts = () => {
    const ghostPositions = [];
    const positions = [
      { x: GRID_SIZE - 2, y: 1 },
      { x: 1, y: GRID_SIZE - 2 },
      { x: GRID_SIZE - 2, y: GRID_SIZE - 2 }
    ];
    
    positions.forEach((pos, index) => {
      ghostPositions.push({
        id: index,
        x: pos.x,
        y: pos.y,
        direction: { x: 0, y: 0 }
      });
    });
    
    return ghostPositions;
  };

  const startGame = () => {
    const newGrid = generateGrid();
    setGrid(newGrid);
    setPlayerPos({ x: 1, y: 1 });
    setGhosts(generateGhosts());
    setScore(0);
    setGameActive(true);
    setGameOver(false);
    setGameWon(false);
    setIsInvisible(false);
    setInvisibilityTimer(0);
  };

  const movePlayer = useCallback((dx, dy) => {
    if (!gameActive || gameOver || gameWon) return;

    const newX = playerPos.x + dx;
    const newY = playerPos.y + dy;

    // Check bounds and walls
    if (newX >= 0 && newX < GRID_SIZE && 
        newY >= 0 && newY < GRID_SIZE && 
        grid[newY] && grid[newY][newX] !== CELL_WALL) {
      
      setPlayerPos({ x: newX, y: newY });
      
      // Collect dot
      if (grid[newY][newX] === CELL_DOT) {
        setGrid(prev => {
          const newGrid = prev.map(row => [...row]);
          newGrid[newY][newX] = CELL_EMPTY;
          return newGrid;
        });
        setScore(prev => prev + 10);
        
        // Check win condition
        const dotsRemaining = grid.flat().filter(cell => cell === CELL_DOT).length;
        if (dotsRemaining <= 1) {
          setGameWon(true);
          setGameActive(false);
        }
      }
      
      // Collect power dot
      if (grid[newY][newX] === CELL_POWER_DOT) {
        setGrid(prev => {
          const newGrid = prev.map(row => [...row]);
          newGrid[newY][newX] = CELL_EMPTY;
          return newGrid;
        });
        setScore(prev => prev + 50);
        setIsInvisible(true);
        setInvisibilityTimer(5); // 5 seconds of invisibility
      }
    }
  }, [gameActive, gameOver, gameWon, playerPos, grid]);

  const moveGhosts = useCallback(() => {
    if (!gameActive || gameOver || gameWon) return;

    setGhosts(prevGhosts => {
      return prevGhosts.map(ghost => {
        const directions = [
          { x: 0, y: -1 }, // up
          { x: 0, y: 1 },  // down
          { x: -1, y: 0 }, // left
          { x: 1, y: 0 }   // right
        ];

        // Simple AI: move towards player occasionally, otherwise random
        let newDirection;
        if (Math.random() > 0.7) {
          // Move towards player using ref
          const currentPlayerPos = playerPosRef.current;
          const dx = currentPlayerPos.x - ghost.x;
          const dy = currentPlayerPos.y - ghost.y;
          
          if (Math.abs(dx) > Math.abs(dy)) {
            newDirection = { x: dx > 0 ? 1 : -1, y: 0 };
          } else {
            newDirection = { x: 0, y: dy > 0 ? 1 : -1 };
          }
        } else {
          // Random movement
          newDirection = directions[Math.floor(Math.random() * directions.length)];
        }

        const newX = ghost.x + newDirection.x;
        const newY = ghost.y + newDirection.y;

        // Check if move is valid using ref
        const currentGrid = gridRef.current;
        if (newX >= 0 && newX < GRID_SIZE && 
            newY >= 0 && newY < GRID_SIZE && 
            currentGrid[newY] && currentGrid[newY][newX] !== CELL_WALL) {
          return { ...ghost, x: newX, y: newY, direction: newDirection };
        }
        
        return ghost;
      });
    });
  }, [gameActive, gameOver, gameWon]);

  const checkCollisions = useCallback(() => {
    if (!gameActive || isInvisible) return;

    const collision = ghosts.some(ghost => 
      ghost.x === playerPos.x && ghost.y === playerPos.y
    );

    if (collision) {
      setGameActive(false);
      setGameOver(true);
    }
  }, [gameActive, ghosts, playerPos, isInvisible]);

  const handleKeyPress = useCallback((e) => {
    switch (e.key.toLowerCase()) {
      case 'w':
        movePlayer(0, -1);
        break;
      case 's':
        movePlayer(0, 1);
        break;
      case 'a':
        movePlayer(-1, 0);
        break;
      case 'd':
        movePlayer(1, 0);
        break;
      default:
        break;
    }
  }, [movePlayer]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    if (gameActive) {
      const ghostInterval = setInterval(moveGhosts, 500);
      return () => clearInterval(ghostInterval);
    }
  }, [gameActive, moveGhosts]);

  useEffect(() => {
    checkCollisions();
  }, [checkCollisions]);

  // Invisibility timer effect
  useEffect(() => {
    if (isInvisible && invisibilityTimer > 0) {
      const timer = setTimeout(() => {
        setInvisibilityTimer(prev => {
          if (prev <= 1) {
            setIsInvisible(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isInvisible, invisibilityTimer]);

  const getCellContent = (x, y) => {
    // Check if player is here
    if (playerPos.x === x && playerPos.y === y) {
      return isInvisible ? '👻' : '🟡'; // Show ghost emoji when invisible
    }
    
    // Check if ghost is here
    const ghost = ghosts.find(g => g.x === x && g.y === y);
    if (ghost) return '👻';
    
    // Check grid content
    switch (grid[y]?.[x]) {
      case CELL_DOT: return '•';
      case CELL_POWER_DOT: return '⭐';
      case CELL_WALL: return '';
      default: return '';
    }
  };

  const getCellClass = (x, y) => {
    switch (grid[y]?.[x]) {
      case CELL_WALL: return 'pacman-cell wall';
      case CELL_DOT: return 'pacman-cell path';
      case CELL_POWER_DOT: return 'pacman-cell path power-dot';
      case CELL_EMPTY: return 'pacman-cell path';
      default: return 'pacman-cell wall';
    }
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">👻 Pacman Chase</h1>
        <div className="game-instructions">
          Use WASD keys to move 🟡. Collect all dots while avoiding ghosts 👻! Grab power dots ⭐ for 5 seconds of invisibility!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          Score: {score}
          {isInvisible && (
            <div style={{color: 'cyan', fontWeight: 'bold'}}>
              👻 INVISIBLE ({invisibilityTimer}s)
            </div>
          )}
          {gameWon && <div style={{color: 'green'}}>🎉 You Won!</div>}
          {gameOver && <div style={{color: 'red'}}>💀 Game Over!</div>}
        </div>
        
        <div className="game-play-area">
          <div className="pacman-grid">
            {grid.map((row, y) =>
              row.map((cell, x) => (
                <div
                  key={`${x}-${y}`}
                  className={getCellClass(x, y)}
                >
                  {getCellContent(x, y)}
                </div>
              ))
            )}
          </div>
        </div>

        {(gameOver || gameWon) && (
          <div className="game-over">
            <h2>{gameWon ? '🎉 Victory!' : '👻 Game Over!'}</h2>
            <p>Final Score: {score}</p>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver || gameWon ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PacmanChase;